import React, { useState } from 'react';
import { getImageUrl } from '@/lib/utils/image';

export interface FlyerCardData {
  title?: string;
  description?: string;
  imageUrl?: string;
  metadata: {
    language: string;
    playerName: string;
    contact?: string;
    feature?: string;
    print?: 'Xerox' | 'Risograph' | 'Digital';
    picture?: string;
    text?: string;
    title?: string;
    date?: string;
    location?: string;
    price?: string;
  };
}

interface FlyerCardProps {
  cardData: FlyerCardData;
}

export const FlyerCard: React.FC<FlyerCardProps> = ({ cardData }) => {
  const [imageError, setImageError] = useState(false);
  
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log('Image load error:', e);
    setImageError(true);
    e.currentTarget.onerror = null;
  };

  const imageUrl = getImageUrl(cardData.imageUrl);
  const printStyle = cardData.metadata.print || 'Digital';

  // Style classes based on print type
  const getPrintStyleClasses = () => {
    switch (printStyle) {
      case 'Xerox':
        return 'bg-zinc-100 grayscale contrast-150';
      case 'Risograph':
        return 'bg-rose-50 mix-blend-multiply';
      default:
        return 'bg-slate-100';
    }
  };

//   console.log('FlyerLayout rendering with data:', cardData);

  return (
    <div className="w-80 min-h-[500px] relative overflow-hidden shadow-lg bg-white">
      {/* Main flyer container with print-specific styling */}
      <div className={`w-full h-full relative ${getPrintStyleClasses()}`}>
        {/* Texture overlays */}
        {/* Main content container */}
        <div className="relative p-6 space-y-6 z-10 text-black">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold tracking-wider">
              {cardData.title || 'Untitled'}
            </h1>
            {cardData.metadata.feature && (
              <div className="text-lg font-semibold text-black">
                {cardData.metadata.feature}
              </div>
            )}
          </div>

          {/* Main Image */}
          <div className="relative aspect-video overflow-hidden rounded-lg shadow-md">
            {imageError ? (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                <span className="text-black">Image unavailable</span>
              </div>
            ) : (
              <img
                src={imageUrl}
                alt={cardData.title}
                className="w-full h-full object-cover"
                onError={handleImageError}
              />
            )}
          </div>

          {/* Description */}
          <div className="text-center text-lg leading-relaxed">
            {cardData.description}
          </div>

          {/* Details Grid */}
          {(cardData.metadata.date || cardData.metadata.location || cardData.metadata.price) && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              {cardData.metadata.date && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">When:</span>
                  <span>{cardData.metadata.date}</span>
                </div>
              )}
              {cardData.metadata.location && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Where:</span>
                  <span>{cardData.metadata.location}</span>
                </div>
              )}
              {cardData.metadata.price && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">Price:</span>
                  <span>{cardData.metadata.price}</span>
                </div>
              )}
            </div>
          )}

          {/* Contact Information */}
          {/* {cardData.metadata.Contact && (
            <div className="text-center border-t border-dashed border-gray-300 pt-4">
              <div className="font-semibold">Contact:</div>
              <div>11-4546-3321</div>
            </div>
          )} */}

          {/* Footer */}
          {/* <div className="text-center font-bold text-xs text-black pt-4 border-t border-gray-200">
            <div>{cardData.metadata.playerName}</div>
            <div>{cardData.metadata.language.toUpperCase()}</div>
          </div> */}
        </div>

        {/* Print-specific effects */}
        {printStyle === 'Xerox' && (
          <div className="absolute inset-0 mix-blend-overlay opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-black via-transparent to-black" />
          </div>
        )}
        {printStyle === 'Risograph' && (
          <div className="absolute inset-0 mix-blend-color opacity-20">
            <div className="w-full h-full bg-gradient-to-r from-rose-500 to-blue-500" />
          </div>
        )}
      </div>
    </div>
  );
};

export default FlyerCard; 